import { Volume2, VolumeX } from 'lucide-react';
import { A, fmtUsd } from '@/constants/journey';
import { useLive } from '@/lib/live';

const XIcon = ({ size = 15 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const TelegramIcon = ({ size = 17 }) => (
  <svg width={size} height={size} viewBox="0 0 496 512" fill="currentColor" aria-hidden="true">
    <path d="M248 8C111 8 0 119 0 256s111 248 248 248 248-111 248-248S385 8 248 8zm114 170-41 191c-3 14-11 17-22 11l-62-46-30 29c-3 3-6 6-12 6l4-63 115-104c5-4-1-7-8-2l-142 89-61-19c-13-4-14-13 3-20l239-92c11-4 21 2 17 20z" />
  </svg>
);

export const StickyTopBar = () => {
  const { state, config, soundOn, soundLoading, toggleSound, setTicketOpen } = useLive();
  const ticker = config?.placeholders?.TOKEN_TICKER || 'THOMAS';
  const xUrl = config?.placeholders?.X_URL;
  const telegramUrl = config?.placeholders?.TELEGRAM_URL;
  const currentMcap = Number(state?.currentMcapUsd);
  const hasLiveMcap = Number.isFinite(currentMcap) && currentMcap > 0;

  return (
    <header className="fixed inset-x-0 top-0 z-50 flex items-center justify-between gap-2 px-3 py-2 sm:px-5">
      <div className="enamel flex items-center gap-2.5 rounded-xl px-3 py-1.5">
        <img src={A.ui.coinFace} alt="Thomas coin face" className="h-7 w-7 rounded-full" />
        <span className="font-display text-lg tracking-wide text-[#f3e7cf]">THOMAS</span>
        <span className="hidden font-mono text-[10px] tracking-[0.18em] text-[#c9a24a] sm:inline" data-testid="topbar-ticker">
          {ticker}
        </span>
        {state && (
          <span className="hidden items-center gap-1.5 border-l border-white/10 pl-2.5 font-mono text-[10px] text-[#f3e7cf]/70 md:flex">
            {hasLiveMcap ? fmtUsd(currentMcap) : 'LIVE'}
          </span>
        )}
      </div>

      <div className="flex items-center gap-2">
        <div className="enamel flex items-center gap-0.5 rounded-xl p-1" aria-label="Official Thomas social channels" data-live-revision="header-social-v2">
          {xUrl && (
            <a
              href={xUrl}
              target="_blank"
              rel="noreferrer"
              className="btn-mech flex h-8 w-8 items-center justify-center rounded-lg text-[#f3e7cf]/78 transition-colors hover:bg-white/10 hover:text-[#f3e7cf] sm:h-9 sm:w-9"
              aria-label="Open the official Thomas X account"
              data-testid="header-social-x"
            >
              <XIcon size={14} />
            </a>
          )}
          {telegramUrl && (
            <a
              href={telegramUrl}
              target="_blank"
              rel="noreferrer"
              className="btn-mech flex h-8 w-8 items-center justify-center rounded-lg text-[#f3e7cf]/78 transition-colors hover:bg-white/10 hover:text-[#f3e7cf] sm:h-9 sm:w-9"
              aria-label="Open the official Thomas Telegram channel"
              data-testid="header-social-telegram"
            >
              <TelegramIcon size={16} />
            </a>
          )}
        </div>
        <button
          type="button"
          onClick={toggleSound}
          className="enamel btn-mech flex h-10 w-10 items-center justify-center rounded-xl text-[#f3e7cf]/85 sm:h-11 sm:w-11"
          aria-label={soundOn ? 'Turn soundtrack and locomotive audio off' : 'Turn soundtrack and locomotive audio on'}
          aria-pressed={soundOn}
          aria-busy={soundLoading}
          disabled={soundLoading}
          data-testid="sound-toggle-button"
        >
          {soundOn ? <Volume2 size={17} /> : <VolumeX size={17} className={soundLoading ? 'animate-pulse' : ''} />}
        </button>
        <button
          type="button"
          onClick={() => setTicketOpen(true)}
          className="brass btn-mech h-10 rounded-xl px-2.5 font-mono text-[10px] tracking-[0.1em] text-[#f3e7cf] sm:h-11 sm:px-4 sm:text-xs sm:tracking-[0.14em]"
          data-testid="board-train-button"
        >
          <span className="sm:hidden">BOARD</span>
          <span className="hidden sm:inline">BOARD THE TRAIN</span>
        </button>
      </div>
    </header>
  );
};
